/**
 *
 * @description 处理价格小数点，千分位
 * @param price String  // 具体金额
 * @param obj Object
 * @return obj Object
 * @time: 2022/1/24 4:51 下午
 *
 */
export function getFormatPriceAmount(obj) {
  let { price, country, isParseInt } = obj;
  const { currency, code } = country;
  let amount = Number(price); // 把price amount转成数值，避免出现字符串的情况
  let formatPriceText = '';

  if (isParseInt) {
    amount = parseInt(amount);
  }

  if (amount % 1 === 0) {
    amount = amount.toString();
  } else {
    const times = Math.pow(10, 2);
    amount = parseInt(amount > 0 ? amount * times + 0.5 : amount * times - 0.5, 10) / times;
    amount = amount.toFixed(2).toString();
  }

  switch (currency) {
    case 'EUR':
      obj.signalSpace = ' ';
      formatPriceText = amount.replace(/\d{1,3}(?=(\d{3})+(\.|$))/g, '$&,');
      if (['DE', 'ES', 'NO', 'IT', 'PT', 'AT', 'BE', 'NL'].includes(code)) {
        // 货币符号后面，句号千分位，逗号小数点 - [x.xxx,xx €]
        amount = amount.replace('.', ',');
        formatPriceText = amount.replace(/\d{1,3}(?=(\d{3})+(,|$))/g, '$&.');
      } else if (['FI', 'FR', 'LU', 'MC', 'SE'].includes(code)) {
        // 货币符号后面，空格千分位，逗号小数点 - [x xxx,xx €]
        amount = amount.replace('.', ',');
        formatPriceText = amount.replace(/\d{1,3}(?=(\d{3})+(,|$))/g, '$& ');
      } else if (
        [
          'AD',
          'BG',
          'CH',
          'CY',
          'CZ',
          'DK',
          'EE',
          'GR',
          'HR',
          'HU',
          'IE',
          'IS',
          'LI',
          'LT',
          'LV',
          'MT',
          'PL',
          'RO',
          'RU',
          'SI',
          'SK',
          'TR',
        ].includes(code)
      ) {
        // 货币符号前面，逗号千分位，句号小数点 - [€ x,xxx.xx]
      }
      break;
    default:
      formatPriceText = amount.replace(/\d{1,3}(?=(\d{3})+(\.|$))/g, '$&,');
  }
  obj.priceText = formatPriceText;
  return obj;
}

/*
 *
 * @description 处理signal
 * @param signal string 货币符号
 * @param currency string 货币
 * @return obj Object
 * @time: 2022/1/24 4:56 下午
 *
 */
export function getFormatSignal(obj) {
  let { signal, currency, country } = obj;
  switch (true) {
    case currency === 'JPY':
      signal = '円';
      break;
    case currency === 'GBP':
      signal = '£';
      break;
    default:
  }

  // 例如：8k直播等特殊商品，由价格本身的货币决定
  if (signal !== country.signal) {
    obj.showSignal = true;
  }

  obj.signalText = signal;
  return obj;
}

/*
 *
 * @description 根据不同货币/地区显示前缀
 * @param {type} params
 * @param obj Object
 * @return obj Object
 * @time 2022/1/24 7:55 下午
 *
 */
function getFormatCurrency(obj) {
  let { currency, country } = obj;
  obj.shouldShowCurrency = true;
  switch (true) {
    case currency === 'TWD':
      currency = 'NT';
      break;
    case currency === 'HKD':
      currency = 'HK';
      break;
    case currency === 'SGD':
      currency = 'S';
      break;
    case currency === 'AUD':
      obj.shouldShowCurrency = true;
      currency = 'AU';
      break;
    case currency === 'CAD':
      obj.shouldShowCurrency = true;
      currency = 'CA';
      break;
    case currency === 'USD' && country.code !== 'US':
      obj.shouldShowCurrency = true;
      currency = 'US';
      break;
    default:
      obj.shouldShowCurrency = false;
  }
  if (currency !== country.currency) {
    obj.shouldShowCurrency = true;
  }
  obj.currencyText = currency;
  return obj;
}

/*
 *
 * @description 获取货币符号位置，前缀 || 后缀
 * @param country Object 国家/地区信息
 * @param currency String 当前要显示的货币
 * @return position String 货币符号位置
 * @time: 2020/11/19 11:24
 *
 */
export function getSignalPosition({ currency, country }) {
  let position = 'prefix';
  switch (true) {
    case ['DE', 'ES', 'PT', 'FI', 'FR', 'IT', 'LU', 'MC', 'SE'].includes(country.code) && currency === 'EUR':
    case ['NO'].includes(country.code) && currency === 'USD':
    case ['JP'].includes(country.code) && currency === 'JPY':
      position = 'suffix';
      break;
    default:
  }
  return position;
}

/**
 * @description 格式化金额
 * @param {Object} priceObj - 金额
 * @param {Number} priceObj.price - 金额
 * @param {String} [priceObj.signal] 货币符号 - 默认取当前国家货币符号
 * @param {String} [priceObj.currency]
 * @param {Boolean} [priceObj.showSignal] 是否显示货币符号 - 默认显示
 * @param {Boolean} [priceObj.showCurrency] 是否显示币种 - 默认不显示
 * @param {Boolean} [priceObj.isParseInt] 是否取整 - 默认不取整
 * @param {Object} [priceObj.country]
 *        1. 用户当前地区信息
 *        2.用户订单所在地区信息（通过订单信息进行构造orderCountry）
 *
 * 注意：
 * 减价展现形式，负号改成放在最前面，负号和数值中间带空格，即：$-19 => - $19
 *
 */

export function formatPriceThousands({
  price,
  signal,
  currency,
  showSignal = true,
  showCurrency = true,
  isParseInt,
  country,
}) {
  const obj = {
    country,
    price,
    signal: signal || country.signal,
    currency: currency || country.currency,
    priceText: '',
    signalText: '',
    currencyText: '',
    signalSpace: '',
    signalPosition: '',
    showSignal,
    showCurrency,
    isParseInt,
  };

  let isDiscountPriceAmount = obj.price < 0;
  let result = '';

  // 折扣金额先转正，最后在前面加上负号
  if (isDiscountPriceAmount) {
    obj.price *= -1;
  }

  getFormatPriceAmount(obj); // x,xxx.xx
  getFormatSignal(obj); // €
  getFormatCurrency(obj); // USD/CAD/A/HK/NT...

  obj.signalPosition = getSignalPosition({ country, currency: obj.currencyText });

  if (obj.signalPosition === 'prefix') {
    result = `${showSignal ? obj.signalText : ''}${obj.signalSpace}${obj.priceText}`;
  } else {
    result = `${obj.priceText}${obj.signalSpace}${showSignal ? obj.signalText : ''}`;
  }

  return `${isDiscountPriceAmount ? '- ' : ''}${
    obj.showCurrency && obj.shouldShowCurrency ? obj.currencyText : ''
  }${result}`;
}
